import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Truck, Snowflake, Apple, Sparkles, Package, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SUPPLIER_ICONS = {
  froid: Truck,
  surgele: Snowflake,
  fruits_legumes: Apple,
  hygiene: Sparkles,
  divers: Package
};

const SUPPLIER_COLORS = {
  froid: 'bg-blue-100 text-blue-700',
  surgele: 'bg-cyan-100 text-cyan-700',
  fruits_legumes: 'bg-green-100 text-green-700',
  hygiene: 'bg-purple-100 text-purple-700',
  divers: 'bg-orange-100 text-orange-700'
};

const SUPPLIER_LABELS = {
  froid: 'Froid',
  surgele: 'Surgelé',
  fruits_legumes: 'Fruits & Légumes',
  hygiene: 'Hygiène',
  divers: 'Divers'
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const SuppliersPage = () => {
  const { api } = useAuth();
  const { setSelectedSupplier, clearCart } = useCart();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await api.get('/suppliers');
        setSuppliers(response.data);
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSuppliers();
  }, [api]);

  const handleSelectSupplier = (supplier) => {
    clearCart();
    setSelectedSupplier(supplier);
    navigate('/catalog');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="suppliers-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Fournisseurs</h1>
        <p className="text-gray-500 mt-1">Sélectionnez un fournisseur pour passer commande</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {suppliers.map((supplier) => {
          const Icon = SUPPLIER_ICONS[supplier.type] || Package;
          const colorClass = SUPPLIER_COLORS[supplier.type] || 'bg-gray-100 text-gray-700';
          
          return (
            <Card 
              key={supplier.id}
              className="border border-gray-200 hover:border-[#1B4332]/50 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => handleSelectSupplier(supplier)}
              data-testid={`supplier-card-${supplier.name}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <Badge variant="outline" className={colorClass}>
                    {SUPPLIER_LABELS[supplier.type]}
                  </Badge>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#1B4332] transition-colors">
                  {supplier.name}
                </h3>

                <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                  <Calendar className="w-4 h-4" />
                  <span>Livraisons:</span>
                  <div className="flex gap-1">
                    {supplier.delivery_days.map((day) => (
                      <span 
                        key={day} 
                        className="px-2 py-0.5 bg-[#D8F3DC] text-[#1B4332] rounded text-xs font-medium"
                      >
                        {DAYS[day]}
                      </span>
                    ))}
                  </div>
                </div>

                <Button 
                  className="w-full bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                  data-testid={`select-supplier-${supplier.name}-btn`}
                >
                  Commander
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {suppliers.length === 0 && (
        <div className="text-center py-12">
          <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Aucun fournisseur disponible</h3>
          <p className="text-gray-500">Contactez votre administrateur pour ajouter des fournisseurs.</p>
        </div>
      )}
    </div>
  );
};

export default SuppliersPage;
